import React, { useEffect, useRef, useCallback } from 'react';

const PickupMap = ({ dispensaries, onMarkerClick, userLocation }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markers = useRef([]);

  // Function to load Google Maps script dynamically
  const loadGoogleMapsScript = () => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        resolve();
      } else {
        const existingScript = document.getElementById('google-maps-script');
        if (existingScript) {
          existingScript.onload = resolve;
          existingScript.onerror = reject;
        } else {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
          script.id = 'google-maps-script';
          script.async = true;
          script.defer = true;
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        }
      }
    });
  };

  // Initialize map
  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) return;

    if (!mapInstance.current) {
      const initialCenter = userLocation || { lat: 34.0522, lng: -118.2437 }; // Use user location or default to LA
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: initialCenter,
        zoom: 12,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "poi",
            elementType: "geometry",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "transit",
            stylers: [{ visibility: "off" }]
          }
        ],
        clickableIcons: false
      });
      console.log('Map initialized with center:', initialCenter);
    }
  };

  // Create custom marker icon from vendor logo
  const createCustomMarker = (logoUrl) => {
    return {
      url: logoUrl,
      scaledSize: new window.google.maps.Size(40, 40),
      origin: new window.google.maps.Point(0, 0),
      anchor: new window.google.maps.Point(20, 20),
      borderRadius: '50%',
    };
  };

  // Add markers to the map
  const addMarkers = useCallback(() => {
    if (!mapInstance.current || !dispensaries?.length) {
      console.warn('No dispensaries or map instance available to add markers.');
      return;
    }

    // Clear previous markers
    markers.current.forEach((marker) => marker.setMap(null));
    markers.current = [];

    // Create bounds object to fit all markers
    const bounds = new window.google.maps.LatLngBounds();

    // Add user location marker if available
    if (userLocation) {
      const userMarker = new window.google.maps.Marker({
        position: userLocation,
        map: mapInstance.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        title: 'Your Location'
      });
      bounds.extend(userLocation);
    }

    dispensaries.forEach((dispensary) => {
      if (!dispensary.coordinates || dispensary.coordinates.length !== 2) {
        console.warn(`Invalid coordinates for ${dispensary.dispensaryName}`);
        return;
      }

      const [lng, lat] = dispensary.coordinates;
      const position = { lat, lng };

      // Create marker with custom icon
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstance.current,
        icon: createCustomMarker(dispensary.logoUrl || 'https://weedhurry-vendor-logos.s3.us-east-2.amazonaws.com/miniLogoBlack.jpg'),
        title: dispensary.dispensaryName
      });

      bounds.extend(position);
      marker.addListener('click', () => onMarkerClick?.(dispensary));
      markers.current.push(marker);
    });

    // Fit map to show all markers with padding
    if (markers.current.length > 0) {
      mapInstance.current.fitBounds(bounds, {
        padding: { top: 50, right: 50, bottom: 50, left: 50 }
      });
    }
  }, [dispensaries, onMarkerClick, userLocation]);

  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => {
        console.log('Google Maps script loaded successfully.');
        initializeMap();
        addMarkers();
      })
      .catch((error) => {
        console.error('Error loading Google Maps script:', error);
      });
  }, [addMarkers]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }}></div>;
};

export default PickupMap;